import { Accordion, Box, Heading, Link as CLink, List, Span, Stack, Text } from '@chakra-ui/react'
import type { PublicJoinResponse } from '@ipc/shared'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useParams } from 'react-router'
import { api } from '~/api/client'
import { keys, useRegistration } from '~/api/hooks'
import { Rich, useT } from '~/i18n/use-t'
import { useFlowSteps, type StepKey } from '~/lib/steps'
import { CameraHelp } from './CameraHelp'
import type { FaqId } from './faq-ids'
import { LocationHelp } from './LocationHelp'

/** What an entry may read besides its own copy (spec §4.1): the flow, the document step and the provider. */
interface Ctx {
  steps: readonly StepKey[]
  /** True on the registration steps after the email step. */
  flow: boolean
  /** The sheet shows "Finish later or stop" under the FAQ (the flow, and an email request applies). */
  requests: boolean
  /** The provider of the page, when the URL names one and the answer has arrived. */
  join: PublicJoinResponse | undefined
}

const FIRST_POINT = {
  kept_for_review: 'aboutFace.keptForReview',
  kept_to_run_service: 'aboutFace.keptToRunService',
  not_kept: 'aboutFace.notKept',
  test_provider_keeps: 'testProvider.aboutFace1',
} as const

/** A link to the privacy page, in a new tab so the registration page behind the sheet is never lost. */
const privacyLink = <CLink href='/privacy' target='_blank' rel='noopener noreferrer' textDecoration='underline' />
const geonamesLink = (
  <CLink href='https://www.geonames.org/' target='_blank' rel='noopener noreferrer' textDecoration='underline' />
)

function FaceData({ join }: { join: PublicJoinResponse | undefined }) {
  const { t } = useT()
  const notice = join?.faceNotice ?? null
  const values = { provider: join?.displayName ?? '' }
  return (
    <Stack gap={3}>
      <Text>
        <Rich k='aboutFace.generic' />
      </Text>
      <Text>
        <Rich k='aboutFace.template' />
      </Text>
      {join && notice && (
        <Text>
          <Rich k={FIRST_POINT[notice]} values={values} />
        </Text>
      )}
      {join && notice === 'test_provider_keeps' && (
        <Text>
          <Rich k='testProvider.aboutFace2' values={values} />
        </Text>
      )}
      <Text>
        <Rich k='aboutFace.reviewMedia' />
      </Text>
      <Text>{t('aboutFace.delete')}</Text>
      <Text>
        <Rich k='faq.faceData.more' components={{ a: privacyLink }} />
      </Text>
    </Stack>
  )
}

function FaceStart({ join }: { join: PublicJoinResponse | undefined }) {
  const { t } = useT()
  const provider = join?.displayName ?? ''
  const testProvider = join?.publicSettings.testProvider === true
  return (
    <Stack gap={3}>
      {join?.flow === 'hosted_redirect' ? (
        <>
          <Text>{t('face.handoverHosted1', { provider })}</Text>
          <Text>{t('face.handoverHosted2')}</Text>
        </>
      ) : join?.flow === 'own_capture' ? (
        <Text>{t('face.handoverOwn', { provider })}</Text>
      ) : join?.flow === 'embedded_sdk' ? (
        <Text>{t(testProvider ? 'testProvider.handover' : 'face.handoverEmbedded', { provider })}</Text>
      ) : (
        <Text>{t('faq.faceStart.generic')}</Text>
      )}
      <Text>
        <Rich k='faq.faceStart.permission' />
      </Text>
    </Stack>
  )
}

function Tips({ join }: { join: PublicJoinResponse | undefined }) {
  const { t } = useT()
  const embedded = join?.flow === 'embedded_sdk'
  const testProvider = join?.publicSettings.testProvider === true
  const more = !embedded
    ? []
    : testProvider
      ? ['testProvider.tipPhone', 'testProvider.tipComputer']
      : ['tips.screen', 'tips.upright', 'tips.laptop', 'tips.glasses']
  return (
    <List.Root ps={6} gap={1}>
      {['tips.light', 'tips.sun', 'tips.cover', 'tips.centre', ...more].map((k) => (
        <List.Item key={k}>{t(k)}</List.Item>
      ))}
    </List.Root>
  )
}

/** The answers. A `Record<FaqId, …>` so an id in `FAQ_IDS` without an answer is a type error. */
const ANSWER: Record<FaqId, (c: Ctx) => ReactNode> = {
  steps: ({ steps }) => <StepsAnswer steps={steps} />,
  needs: () => <Plain k='faq.needs.a' />,
  ownEmail: () => <Plain k='faq.ownEmail.a' />,
  later: ({ requests }) => <Plain k={requests ? 'faq.later.a' : 'faq.later.aOutside'} />,
  code: () => <Plain k='faq.code.a' />,
  codeButton: () => <Plain k='faq.codeButton.a' rich />,
  // The GeoNames credit (CC BY 4.0) of the city search lives here, off the step itself (owner, 2026-10-30).
  place: () => (
    <Stack gap={3}>
      <Plain k='faq.place.a' />
      <Text color='fg.muted'>
        <Rich k='place.geonames' components={{ a: geonamesLink }} />
      </Text>
    </Stack>
  ),
  location: () => <Plain k='faq.location.a' />,
  locationAllow: () => <LocationHelp />,
  face: () => <Plain k='faq.face.a' />,
  faceData: ({ join }) => <FaceData join={join} />,
  faceStart: ({ join }) => <FaceStart join={join} />,
  tips: ({ join }) => <Tips join={join} />,
  cameraAllow: ({ flow }) => (
    <Stack gap={4}>
      <Plain k='faq.cameraAllow.intro' />
      <CameraHelp flow={flow} />
    </Stack>
  ),
  faceFailed: () => <Plain k='faq.faceFailed.a' />,
  document: () => <Plain k='faq.document.a' rich />,
  documentStart: () => <Plain k='faq.documentStart.a' />,
  password: () => <Plain k='password.tips' />,
  forgot: () => <Plain k='faq.forgot.a' />,
  delete: ({ requests }) => <Plain k={requests ? 'faq.delete.a' : 'faq.delete.aOutside'} />,
}

function Plain({ k, rich = false }: { k: string; rich?: boolean }) {
  const { t } = useT()
  return <Text>{rich ? <Rich k={k} components={{ a: privacyLink }} /> : t(k)}</Text>
}

function StepsAnswer({ steps }: { steps: readonly StepKey[] }) {
  const { t } = useT()
  return (
    <Stack gap={3}>
      <List.Root as='ol' ps={6} gap={1}>
        {steps.map((key) => (
          <List.Item key={key}>{t(`welcome.step_${key}`)}</List.Item>
        ))}
      </List.Root>
      <Text>{t('faq.steps.after')}</Text>
    </Stack>
  )
}

const GROUPS: { id: string; document?: true; entries: readonly FaqId[] }[] = [
  { id: 'registering', entries: ['steps', 'needs', 'ownEmail', 'later'] },
  { id: 'code', entries: ['code', 'codeButton'] },
  { id: 'place', entries: ['place', 'location', 'locationAllow'] },
  { id: 'face', entries: ['face', 'faceData', 'faceStart', 'tips', 'cameraAllow', 'faceFailed'] },
  { id: 'document', document: true, entries: ['document', 'documentStart'] },
  { id: 'account', entries: ['password', 'forgot', 'delete'] },
]

/**
 * True while a request for an email applies: after the email is confirmed and before the registration becomes the
 * voter session (an approved or review decision is claimed, passwordless spec §2).
 */
export function useCanEmailRequest(): boolean {
  const s = useRegistration().data
  return !!s && s.stage !== 'email_pending' && !s.claimable && s.status !== 'erased'
}

/**
 * The FAQ (spec §4): a grouped accordion. It reads its own context (the flow's steps, the provider named in the URL,
 * the organisation), so a page passes nothing but `openId`: that entry starts expanded, is scrolled to the top of the
 * sheet and gets the focus (the Help sheet also names it as its initial focus).
 */
export function Faq({ openId, flow }: { openId?: FaqId; flow: boolean }) {
  const { t } = useT()
  const { providerId } = useParams()
  const { required, steps } = useFlowSteps()
  const [value, setValue] = useState<string[]>(openId ? [openId] : [])
  const root = useRef<HTMLDivElement>(null)
  // The provider's answer is cached by the page that already asked for it (`useJoinInfo`, same key); with no
  // provider in the URL (log-in, member pages) the entries use their generic text.
  const joinQuery = useQuery<PublicJoinResponse>({
    queryKey: keys.join(providerId),
    queryFn: () => api('publicJoin', { params: { providerId: providerId ?? '' } }),
    enabled: !!providerId,
  })
  // "at the end of this help" is only true where the sheet shows the requests (HelpSheet's FinishLater).
  const canRequest = useCanEmailRequest()
  const requests = flow && canRequest
  const ctx: Ctx = { steps, flow, requests, join: providerId ? joinQuery.data : undefined }

  // The entry comes to the top and its trigger takes the focus. The sheet's `initialFocusEl` names the same trigger,
  // but on its own the focus ends on the sheet itself (the deep-link test of faq.test.tsx shows it): this effect is
  // the one that puts it on the entry.
  useEffect(() => {
    if (!openId) return
    const entry = root.current?.querySelector<HTMLElement>(`[data-faq-entry="${openId}"]`)
    entry?.scrollIntoView({ block: 'start' })
    entry?.querySelector<HTMLElement>('button')?.focus({ preventScroll: true })
  }, [openId])

  return (
    <Accordion.Root
      ref={root}
      collapsible
      multiple={false}
      lazyMount
      unmountOnExit
      value={value}
      onValueChange={(e) => setValue(e.value)}
      variant='outline'
    >
      <Stack gap={5}>
        {GROUPS.filter((g) => !g.document || required).map((g) => (
          <Box key={g.id}>
            <Heading as='h3' size='md' mb={2}>
              {t(`faq.group.${g.id}`)}
            </Heading>
            {g.entries.map((id) => (
              <Accordion.Item key={id} value={id} data-faq-entry={id}>
                <Accordion.ItemTrigger minH='12' py={2} gap={3} fontSize='md' fontWeight='medium'>
                  <Span flex='1' textAlign='start'>
                    {t(`faq.${id}.q`)}
                  </Span>
                  <Accordion.ItemIndicator />
                </Accordion.ItemTrigger>
                <Accordion.ItemContent>
                  <Accordion.ItemBody>{ANSWER[id](ctx)}</Accordion.ItemBody>
                </Accordion.ItemContent>
              </Accordion.Item>
            ))}
          </Box>
        ))}
      </Stack>
    </Accordion.Root>
  )
}
